import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { ReservationLink } from '@/entities/capacity'
import {
  cacheShare,
  callCardTelemetry,
  callOutcome,
  getCallsOptions,
  getCallsQueryKey,
  hangupCallMutation,
  intentHintLabel,
  liveKindLabel,
  type MeteringCallSummary,
  type MeteringLiveCall,
} from '@/entities/metering'
import { cn, errorMessage, formatClock, formatDateTime } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card, CardLink } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { ConfirmButton } from '@/shared/ui/confirm-button'
import { EmptyState } from '@/shared/ui/empty-state'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'
import { SearchInput } from '@/shared/ui/search-input'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'

// Доска живёт в URL: её шлют ссылкой, refresh не сбрасывает. Сортировка —
// тоже: «медленные сверху» ведёт к разбору худших звонков смены. Разрез по
// заявке приходит ссылкой с доски ёмкости — «чем занималось её железо».
const searchSchema = z.object({
  tab: z.enum(['live', 'completed']).catch('live'),
  sort: z.enum(['recent', 'slow']).optional().catch(undefined),
  reservation: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/calls')({
  component: CallsPage,
  validateSearch: searchSchema,
  staticData: { crumb: 'Звонки' },
})

// Экспорт нужен компонентному тесту: сам роут собирает его из этого же модуля.
export function CallsPage() {
  const params = Route.useSearch()
  const { tab, reservation } = params
  const calls = useQuery({
    ...getCallsOptions(reservation ? { query: { reservation } } : {}),
    refetchInterval: 10_000,
  })
  const sort = params.sort ?? 'recent'
  const navigate = Route.useNavigate()
  const { callId } = useParams({ strict: false })
  const [search, setSearch] = useState('')

  const live = calls.data?.live ?? []
  const completed = calls.data?.completed ?? []

  // Открытый звонок всегда завершённый — deep-link на него не должен упираться
  // в дефолтную вкладку эфира.
  const board = callId ? 'completed' : tab

  const q = search.trim().toLowerCase()
  const match = (id: string, account: string) =>
    !q || id.toLowerCase().includes(q) || account.toLowerCase().includes(q)

  const filteredLive = live.filter((c) => match(c.call_id, c.account_id))
  const filteredCompleted = completed
    .filter((c) => match(c.call_id, c.account_id))
    .sort((a, b) =>
      sort === 'slow'
        ? callCardTelemetry(b).perceivedP50Ms - callCardTelemetry(a).perceivedP50Ms
        : 0,
    )

  return (
    <>
      <PageHeader title="Звонки" description="Живая доска и завершённые звонки с отчётами" />

      {reservation && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-control bg-raise px-4 py-2.5 text-body-sm">
          <span className="text-muted">Разрез по заявке на ёмкость:</span>
          <span className="font-mono">{reservation}</span>
          <span className="text-caption text-muted">обе доски — и эфир, и завершённые</span>
          <Button
            size="sm"
            onClick={() =>
              void navigate({ to: '/calls', search: { tab: params.tab, sort: params.sort } })
            }
          >
            Снять фильтр
          </Button>
        </div>
      )}

      {calls.isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : calls.isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить доску"
          description={errorMessage(calls.error)}
          action={
            <Button size="sm" onClick={() => void calls.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PillTabs
              aria-label="Доска звонков"
              value={board}
              onChange={(value) =>
                void navigate({
                  to: '/calls',
                  search: { tab: value, sort: params.sort, reservation },
                })
              }
              options={[
                { value: 'live', label: 'В эфире', count: live.length },
                { value: 'completed', label: 'Завершённые', count: completed.length },
              ]}
            />
            {board === 'completed' && (
              <PillTabs
                aria-label="Порядок завершённых"
                value={sort}
                onChange={(value) =>
                  void navigate({
                    to: '/calls',
                    search: { tab: 'completed', sort: value, reservation },
                  })
                }
                options={[
                  { value: 'recent', label: 'Свежие' },
                  { value: 'slow', label: 'Медленные' },
                ]}
              />
            )}
            <SearchInput
              placeholder="Аккаунт или call id…"
              className="w-72"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Поиск звонков"
            />
          </div>

          {board === 'live' ? (
            <LiveBoard calls={filteredLive} />
          ) : (
            <div className="flex items-start gap-6">
              <div className="flex w-[400px] shrink-0 flex-col gap-2">
                {filteredCompleted.length === 0 && (
                  <EmptyState
                    icon="phone-call"
                    title="Пусто"
                    description="Завершённых звонков нет"
                  />
                )}
                {filteredCompleted.map((call) => (
                  <CompletedCard
                    key={call.call_id}
                    call={call}
                    active={callId === call.call_id}
                    reservation={reservation}
                  />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <Outlet />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function LiveBoard({ calls }: { calls: MeteringLiveCall[] }) {
  if (calls.length === 0) {
    return (
      <EmptyState icon="phone-call" title="Эфир пуст" description="Сейчас нет активных звонков" />
    )
  }
  return (
    <div className="flex flex-col gap-2">
      {calls.map((call) => (
        <LiveRow key={call.call_id} call={call} />
      ))}
    </div>
  )
}

function LiveRow({ call }: { call: MeteringLiveCall }) {
  const qc = useQueryClient()
  const hangup = useMutation({
    ...hangupCallMutation(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: getCallsQueryKey() }),
  })

  return (
    <Card className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
      <span className="relative flex size-2.5 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-ok opacity-60" />
        <span className="relative inline-flex size-2.5 rounded-full bg-ok" />
      </span>
      <div className="min-w-0 flex-1 basis-52">
        <p className="truncate font-mono text-body-sm font-medium">{call.call_id}</p>
        <p className="text-caption text-faint">{formatDateTime(call.at)}</p>
      </div>
      <Chip>{call.account_id}</Chip>
      {/* Заявка живого звонка — «чьё железо сейчас занято»: без неё эфир не
          сводится с доской ёмкости. */}
      {call.reservation_id && <ReservationLink id={call.reservation_id} />}
      <span className="text-body-sm text-muted">{liveKindLabel(call.kind)}</span>
      <ConfirmButton
        size="sm"
        confirmLabel="Разорвать?"
        pending={hangup.isPending}
        onConfirm={() => hangup.mutate({ path: { call_id: call.call_id } })}
      >
        Завершить
      </ConfirmButton>
      {hangup.isError && (
        <p className="w-full text-caption text-danger">{errorMessage(hangup.error)}</p>
      )}
    </Card>
  )
}

function CompletedCard({
  call,
  active,
  reservation,
}: {
  call: MeteringCallSummary
  active: boolean
  reservation: string | undefined
}) {
  const outcome = callOutcome(call.outcome)
  const tele = callCardTelemetry(call)
  const cache = cacheShare(tele.cachedTokens, tele.promptTokens)
  const dim = active ? 'text-white/70' : 'text-muted'
  return (
    <CardLink
      to="/calls/$callId"
      params={{ callId: call.call_id }}
      search={{ tab: 'completed', reservation }}
      active={active}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-body-sm font-medium">{call.account_id}</span>
        <span className={cn('shrink-0 text-caption', active ? 'text-white/70' : 'text-muted')}>
          {formatDateTime(call.started_at)}
        </span>
      </div>
      <p
        className={cn(
          'mt-0.5 truncate font-mono text-caption',
          active ? 'text-white/70' : 'text-muted',
        )}
      >
        {call.call_id}
      </p>
      <div
        className={cn(
          'mt-3 flex items-center justify-between rounded-control px-3 py-2',
          active ? 'bg-black/25' : 'bg-raise',
        )}
      >
        <StatusBadge {...outcome} />
        <span className={cn('text-caption', dim)}>{formatClock(call.duration_ms)}</span>
      </div>
      {(tele.turns > 0 || tele.persistOk === false || tele.intentHint || call.reservation) && (
        <div className={cn('mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption', dim)}>
          {tele.perceivedP50Ms > 0 && (
            <span className="tabular-nums">
              пауза{' '}
              <span className={cn('font-medium', active ? 'text-white' : 'text-app-fg')}>
                {tele.perceivedP50Ms} мс
              </span>
            </span>
          )}
          {tele.turns > 0 && <span>ходов {tele.turns}</span>}
          {cache !== null && <span className="tabular-nums">кэш {Math.round(cache * 100)}%</span>}
          {tele.intentHint && <span>{intentHintLabel(tele.intentHint)}</span>}
          {tele.prematureEou > 0 && (
            <span className={active ? 'text-white' : 'text-danger'}>
              ранний EOU · {tele.prematureEou}
            </span>
          )}
          {tele.persistOk === false && (
            <span className={active ? 'text-white' : 'text-danger'}>телеметрия потеряна</span>
          )}
          {!reservation && call.reservation && (
            <span className="font-mono">заявка {call.reservation}</span>
          )}
        </div>
      )}
    </CardLink>
  )
}
